/**
 * Gestes TOTP partagés (port de Pimer, 2026-09-16) : l'écran imposé à la connexion
 * (pages/SecondFacteurPage.tsx) et la fenêtre « Sécurité du compte »
 * (components/SecuriteCompteModal.tsx) passent par les mêmes fonctions.
 *
 * Tout passe par Supabase Auth (GoTrue /auth/v1/factors), jamais par le serveur ServUp.
 * Mesuré sur le banc (2026-09-16) :
 *   - retirer un facteur VÉRIFIÉ exige une session aal2 (« AAL2 required… ») ;
 *   - deux facteurs d'un même compte ne peuvent pas porter le même nom ;
 *   - après le retrait du DERNIER facteur, le rafraîchissement rend un jeton aal1.
 */

import { supabase } from '../../SupabaseWrapper';

export interface EnrolementTotp {
  id: string;
  qrCode: string;
  secret: string;
}

export interface FacteurTotp {
  id: string;
  nom: string;
  creeLe: string;
}

export type ResultatVerification = 'ok' | 'incorrect' | 'indisponible';

export async function estAal2(): Promise<boolean> {
  try {
    const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    return data?.currentLevel === 'aal2';
  } catch {
    return false;
  }
}

/** Facteurs TOTP vérifiés du compte connecté, du plus ancien au plus récent. */
export async function listerFacteursTotp(): Promise<FacteurTotp[]> {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error) throw error;
  return (data?.totp || [])
    .filter((f) => f.status === 'verified')
    .map((f) => ({ id: f.id, nom: f.friendly_name || 'Application d’authentification', creeLe: f.created_at }))
    .sort((a, b) => a.creeLe.localeCompare(b.creeLe));
}

/**
 * Prépare un facteur à vérifier : QR code + clé. Un facteur non vérifié laissé par un
 * essai abandonné est retiré d'abord (il bloquerait ou encombrerait le suivant).
 */
export async function preparerEnrolementTotp(): Promise<EnrolementTotp> {
  const { data: existants } = await supabase.auth.mfa.listFactors();
  for (const f of existants?.all || []) {
    if (f.factor_type === 'totp' && f.status !== 'verified') {
      await supabase.auth.mfa.unenroll({ factorId: f.id }).catch(() => undefined);
    }
  }
  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: 'totp',
    friendlyName: `ServUp ${new Date().toISOString()}`,   // unique : GoTrue refuse deux noms identiques
    issuer: 'ServUp'
  });
  if (error) throw error;
  return { id: data.id, qrCode: data.totp.qr_code, secret: data.totp.secret };
}

/** Abandon d'un enrôlement : le facteur non vérifié ne doit pas rester. */
export async function abandonnerEnrolementTotp(factorId: string): Promise<void> {
  await supabase.auth.mfa.unenroll({ factorId }).catch(() => undefined);
}

/**
 * Défi + vérification. Durci comme chez Pimer : avant de conclure « incorrect », on
 * relit le niveau AAL — un verify abouti dont la réponse s'est perdue laisse la
 * session en aal2, et afficher une erreur serait faux.
 * `accepterAal2Existant` : sur l'écran de connexion, une session déjà aal2 suffit ;
 * dans « Sécurité du compte » (déjà aal2), il faut vérifier CE facteur-là.
 */
export async function verifierCodeTotp(factorId: string, code: string, accepterAal2Existant: boolean): Promise<ResultatVerification> {
  if (accepterAal2Existant && await estAal2()) return 'ok';
  const { data: defi, error: errDefi } = await supabase.auth.mfa.challenge({ factorId });
  if (errDefi) {
    if (accepterAal2Existant && await estAal2()) return 'ok';
    return 'indisponible';
  }
  const { error: errVerif } = await supabase.auth.mfa.verify({ factorId, challengeId: defi.id, code: code.trim() });
  if (errVerif) {
    if (accepterAal2Existant && await estAal2()) return 'ok';
    return 'incorrect';
  }
  return 'ok';
}

export async function retirerFacteurTotp(factorId: string): Promise<void> {
  const { error } = await supabase.auth.mfa.unenroll({ factorId });
  if (error) throw error;
}

export function formaterCleTotp(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim();
}
